import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Card, CardActionArea, CardContent, Chip, CircularProgress,
  Container, Grid, LinearProgress, MenuItem, Paper, Slider, Stack, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import { useAuth } from '../hooks/useAuth';
import AppService from '../services/AppService';
import AIService from '../services/AIService';
import AppleCreativeService, { AppleLibraryAsset } from '../services/AppleCreativeService';
import type { App } from '../types/app';
import {
  AppleCrop, ApplePlacement, APPLE_CREATIVE_PRESETS, APPLE_ASSET_GUIDE, APPLE_ASSET_SPECS, getAppleCropRect,
} from '../utils/appleCreative';

type Source = { url: string; name: string; storagePath?: string; dataUrl?: string };
const centeredCrop: AppleCrop = { x: 50, y: 50, zoom: 1 };

export default function AppleCreativeAssets() {
  const { user } = useAuth();
  const [apps, setApps] = useState<App[]>([]);
  const [appId, setAppId] = useState('');
  const [placement, setPlacement] = useState<ApplePlacement>('header');
  const [presetIndex, setPresetIndex] = useState(0);
  const [model, setModel] = useState('gpt-image-2.5-flare');
  const [direction, setDirection] = useState('');
  const [source, setSource] = useState<Source | null>(null);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [crop, setCrop] = useState<AppleCrop>(centeredCrop);
  const [assets, setAssets] = useState<AppleLibraryAsset[]>([]);
  const [refresh, setRefresh] = useState(0);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [libraryError, setLibraryError] = useState('');
  const [busy, setBusy] = useState<'generate' | 'export' | 'download' | 'upload' | null>(null);
  const [error, setError] = useState('');
  const [exported, setExported] = useState<(AppleLibraryAsset & { fileName: string }) | null>(null);
  const [dragging, setDragging] = useState(false);
  const canvas = useRef<HTMLCanvasElement>(null);
  const preset = APPLE_CREATIVE_PRESETS[placement][presetIndex];
  const app = apps.find((item) => item.appId === appId);

  useEffect(() => {
    let active = true;
    if (user?.email) AppService.getUserApps(user.email)
      .then((items) => { if (active) setApps(items); })
      .catch(() => { if (active) setError('Could not load your apps. Please reload the page.'); });
    return () => { active = false; };
  }, [user?.email]);

  useEffect(() => {
    let active = true;
    setAssets([]);
    setLibraryError('');
    if (!appId || !user?.email) { setLoadingLibrary(false); return; }
    setLoadingLibrary(true);
    AppleCreativeService.listAssets(appId, user.email)
      .then((items) => { if (active) setAssets(items); })
      .catch(() => { if (active) setLibraryError('Could not load the asset library. Try Refresh.'); })
      .finally(() => { if (active) setLoadingLibrary(false); });
    return () => { active = false; };
  }, [appId, user?.email, refresh]);

  useEffect(() => {
    setImage(null);
    if (!source) return;
    let active = true;
    const next = new Image();
    next.onload = () => { if (active) setImage(next); };
    next.onerror = () => { if (active) setError('Could not load this image. Try another asset or upload a local copy.'); };
    next.src = source.url;
    return () => { active = false; };
  }, [source]);

  useEffect(() => {
    const context = canvas.current?.getContext('2d');
    if (!context || !image) return;
    const rect = getAppleCropRect(image.naturalWidth, image.naturalHeight, preset.width, preset.height, crop);
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, context.canvas.width, context.canvas.height);
    context.drawImage(image, rect.x, rect.y, rect.width, rect.height, 0, 0, context.canvas.width, context.canvas.height);
  }, [image, crop, preset]);

  function chooseSource(next: Source) {
    setSource(next); setCrop(centeredCrop); setExported(null); setError('');
  }

  async function upload(file?: File) {
    if (!file || busy) return;
    setError('');
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || file.size > 3 * 1024 * 1024) {
      setError('Choose a PNG, JPEG, or WebP image up to 3 MB.'); return;
    }
    setBusy('upload');
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error('Could not read this file.'));
        reader.readAsDataURL(file);
      });
      chooseSource({ url: dataUrl, dataUrl, name: file.name });
    } catch (e) { setError(e instanceof Error ? e.message : 'Upload failed.'); }
    finally { setBusy(null); }
  }

  async function generate() {
    if (!app || busy) return;
    setBusy('generate'); setError('');
    try {
      const asset = await AppleCreativeService.generate(app, placement, preset.width, preset.height, direction, model);
      chooseSource({ url: asset.downloadUrl, storagePath: asset.storagePath, name: `${placement} concept` });
      setRefresh((value) => value + 1);
    } catch (e) { setError(e instanceof Error ? e.message : 'Generation failed.'); }
    finally { setBusy(null); }
  }

  async function exportAsset() {
    if (!appId || !source || !image || busy) return;
    setBusy('export'); setError(''); setExported(null);
    try {
      const result = await AIService.exportAppleCreativeAsset({
        appId, placement, width: preset.width, height: preset.height, crop,
        sourceStoragePath: source.storagePath, sourceDataUrl: source.dataUrl,
      });
      setExported(result); setRefresh((value) => value + 1);
    } catch (e) { setError(e instanceof Error ? e.message : 'Export failed.'); }
    finally { setBusy(null); }
  }

  async function download() {
    if (!exported || busy) return;
    setBusy('download'); setError('');
    try {
      const response = await fetch(exported.downloadUrl);
      if (!response.ok) throw new Error('Download failed. Try again.');
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement('a'); link.href = url;
      link.download = `${(app?.name || 'app').replace(/[^a-z0-9-_]/gi, '_')}-${exported.fileName}`;
      document.body.appendChild(link); link.click(); link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { setError(e instanceof Error ? e.message : 'Download failed.'); }
    finally { setBusy(null); }
  }

  function changeCrop(key: keyof AppleCrop, value: number) {
    setCrop((current) => ({ ...current, [key]: value })); setExported(null);
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Box>
          <Chip label="APP STORE CREATIVE" size="small" color="primary" sx={{ mb: 1.5 }} />
          <Typography variant="h4" component="h1" fontWeight={800}>Apple Creative Assets</Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>Create artwork for your product page header and search results, then crop and export at the exact size you need.</Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
            <Button href={APPLE_ASSET_GUIDE} target="_blank" rel="noopener noreferrer" size="small">Apple’s design guide</Button>
            <Button href={APPLE_ASSET_SPECS} target="_blank" rel="noopener noreferrer" size="small">Image specifications</Button>
          </Stack>
        </Box>
        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField select fullWidth label="App" value={appId} disabled={!!busy} onChange={(event) => {
                setAppId(event.target.value); setSource(null); setExported(null); setError(''); setCrop(centeredCrop);
              }}>
                <MenuItem value="">Select a registered app</MenuItem>
                {apps.map((item) => <MenuItem key={item.appId} value={item.appId}>{item.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={7}>
              <Tabs value={placement} aria-label="Apple asset placement" onChange={(_, value: ApplePlacement) => {
                setPlacement(value); setPresetIndex(0); setCrop(centeredCrop); setExported(null);
              }}>
                <Tab value="header" label="Header" disabled={!!busy} />
                <Tab value="search-results" label="Search Results" disabled={!!busy} />
              </Tabs>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {placement === 'header' ? 'Set the tone with a single strong brand visual.' : 'Show what your app offers at a glance.'}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
        <Grid container spacing={3} sx={{ width: 'auto' }}>
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
              <Stack spacing={2.5}>
                <Typography variant="h6" component="h2">1. Create your artwork</Typography>
                <TextField select label="Output size" value={presetIndex} disabled={!!busy} onChange={(event) => {
                  setPresetIndex(Number(event.target.value)); setCrop(centeredCrop); setExported(null);
                }}>
                  {APPLE_CREATIVE_PRESETS[placement].map((item, index) => <MenuItem key={item.label} value={index}>{item.label}</MenuItem>)}
                </TextField>
                <TextField label="Creative direction" multiline minRows={4} value={direction} disabled={!!busy} onChange={(event) => setDirection(event.target.value)} placeholder="Describe a scene, mood, palette, or seasonal theme…" helperText="Your app description and research provide the context." />
                <TextField select label="Image model" value={model} disabled={!!busy} onChange={(event) => setModel(event.target.value)}>
                  <MenuItem value="gpt-image-2.5-flare">GPT Image 2.5 Flare</MenuItem>
                  <MenuItem value="gpt-image-2.5-sunburst">GPT Image 2.5 Sunburst</MenuItem>
                </TextField>
                <Button variant="contained" size="large" startIcon={busy === 'generate' ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />} disabled={!app || !!busy} onClick={generate}>
                  {busy === 'generate' ? 'Generating artwork…' : `Generate ${placement === 'header' ? 'header' : 'search artwork'}`}
                </Button>
                <Typography variant="body2" color="text.secondary">Keep important artwork centered. Avoid prices, URLs, awards, and Apple badges. Review the result in App Store Connect’s Preview tool before submitting.</Typography>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={8}>
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                  <Typography variant="h6" component="h2">2. Crop & resize</Typography>
                  <Chip label={`${preset.width} × ${preset.height} px · PNG`} variant="outlined" />
                </Stack>
                {source ? (
                  <>
                    <Box sx={{ bgcolor: '#101827', p: 1.5, borderRadius: 2 }}>
                      {image ? <canvas ref={canvas} width={900} height={Math.round(900 * preset.height / preset.width)} aria-label={`${placement === 'header' ? 'Header' : 'Search Results'} crop preview`} style={{ width: '100%', height: 'auto', display: 'block' }} /> : <LinearProgress aria-label="Loading image" />}
                    </Box>
                    <Typography variant="caption" color="text.secondary">{source.name}{image ? ` · Source ${image.naturalWidth} × ${image.naturalHeight} px` : ''}</Typography>
                    {image && (image.naturalWidth < preset.width || image.naturalHeight < preset.height) && <Alert severity="info">This image will be upscaled to the selected size. For the sharpest detail, start with a larger original.</Alert>}
                    <Grid container spacing={3} sx={{ width: 'auto' }}>
                      {(['x', 'y', 'zoom'] as const).map((key) => <Grid item xs={12} sm={4} key={key}>
                        <Typography id={`crop-${key}`} variant="body2">{key === 'x' ? 'Horizontal position' : key === 'y' ? 'Vertical position' : 'Zoom'}</Typography>
                        <Slider aria-labelledby={`crop-${key}`} value={crop[key]} min={key === 'zoom' ? 1 : 0} max={key === 'zoom' ? 3 : 100} step={key === 'zoom' ? 0.05 : 1} disabled={!!busy} valueLabelDisplay="auto" onChange={(_, value) => changeCrop(key, Number(value))} />
                      </Grid>)}
                    </Grid>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                      <Button variant="outlined" disabled={!!busy} onClick={() => { setCrop(centeredCrop); setExported(null); }}>Reset crop</Button>
                      <Button variant="contained" disabled={!image || !!busy || !appId} onClick={exportAsset} startIcon={busy === 'export' ? <CircularProgress size={18} color="inherit" /> : undefined}>{busy === 'export' ? 'Preparing PNG…' : 'Resize & save PNG'}</Button>
                      {exported && <Button variant="contained" color="success" startIcon={<DownloadIcon />} disabled={!!busy} onClick={download}>Download PNG</Button>}
                    </Stack>
                    {exported && <Alert severity="success">Saved to your asset library: {exported.width} × {exported.height} px, RGB PNG without an alpha channel.</Alert>}
                  </>
                ) : <Box sx={{ minHeight: 250, display: 'grid', placeItems: 'center', bgcolor: 'action.hover', borderRadius: 2, p: 3 }}><Typography color="text.secondary" textAlign="center">Generate artwork, upload an image, or select one from your app’s asset library to begin.</Typography></Box>}
                <Box onDragOver={(event) => { event.preventDefault(); if (!busy) setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={(event) => { event.preventDefault(); setDragging(false); if (appId) upload(event.dataTransfer.files[0]); }} sx={{ border: '2px dashed', borderColor: dragging ? 'primary.main' : 'divider', borderRadius: 2, p: 2, textAlign: 'center', bgcolor: dragging ? 'action.selected' : undefined }}>
                  <Button component="label" startIcon={<CloudUploadIcon />} disabled={!appId || !!busy}>Upload an image
                    <input type="file" hidden accept="image/png,image/jpeg,image/webp" disabled={!appId || !!busy} onChange={(event) => { upload(event.target.files?.[0]); event.target.value = ''; }} />
                  </Button>
                  <Typography variant="caption" display="block" color="text.secondary">Or drag and drop · PNG, JPEG, WebP · Up to 3 MB</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">Export uses the crop shown above, preserves proportions, and removes transparency on a white background. Apple reviews creative content as well as dimensions.</Typography>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Box><Typography variant="h6" component="h2">Your app’s asset library</Typography><Typography variant="body2" color="text.secondary">Select any image to prepare it for either Apple placement.</Typography></Box>
            <Button disabled={!appId || loadingLibrary || !!busy} onClick={() => setRefresh((value) => value + 1)}>Refresh</Button>
          </Stack>
          {libraryError && <Alert severity="error" sx={{ mb: 2 }}>{libraryError}</Alert>}
          {loadingLibrary ? <LinearProgress /> : !assets.length ? <Typography color="text.secondary">{appId ? 'No saved images yet. Generate artwork or upload an image above.' : 'Select an app to view its images.'}</Typography> :
            <Grid container spacing={2}>{assets.map((asset) => <Grid item xs={6} sm={4} md={3} lg={2} key={asset.assetId}>
              <Card variant="outlined" sx={{ borderColor: source?.storagePath === asset.storagePath ? 'primary.main' : 'divider' }}>
                <CardActionArea disabled={!!busy} onClick={() => chooseSource({ url: asset.downloadUrl, storagePath: asset.storagePath, name: asset.category || 'Library image' })} aria-label={`Select ${asset.category || 'library image'} ${asset.assetId}`}>
                  <Box component="img" loading="lazy" src={asset.downloadUrl} alt={asset.category || 'App artwork'} sx={{ width: '100%', aspectRatio: '3 / 2', objectFit: 'cover', display: 'block' }} />
                  <CardContent sx={{ p: 1.5 }}><Typography variant="caption" display="block" noWrap>{asset.category || 'Image'}</Typography><Typography variant="caption" color="text.secondary">{asset.width && asset.height ? `${asset.width} × ${asset.height}` : asset.model || 'Source artwork'}</Typography></CardContent>
                </CardActionArea>
              </Card>
            </Grid>)}</Grid>}
        </Paper>
      </Stack>
    </Container>
  );
}
